/**
 * Armor classes and the damage matrix (R15).
 *
 * Unit counters are a **data property**, not code. Adding the twenty-fourth
 * unit must not mean touching combat resolution, and rebalancing must be a
 * table edit the bot-match sweep can evaluate automatically (KTD5).
 *
 * There is no air class. Air units are out of scope entirely, so no
 * can-target-air flag is needed and pathfinding stays single-domain.
 */

import * as fix from '../../core/fixed.js'

export const ARMOR = {
  Infantry: 0,
  Light: 1,
  Heavy: 2,
  Structure: 3,
} as const

export type ArmorClass = (typeof ARMOR)[keyof typeof ARMOR]

export const ARMOR_COUNT = 4

export const ARMOR_NAMES = ['infantry', 'light', 'heavy', 'structure'] as const

/**
 * Warheads. A weapon names one; the warhead names a multiplier per armor class.
 * Separating the two means two weapons with different range and cooldown can
 * share counter behaviour without duplicating the row.
 */
export const WARHEAD = {
  SmallArms: 0,
  AutoCannon: 1,
  ApShell: 2,
  HeShell: 3,
  Arc: 4,
  Siege: 5,
  DefenseGun: 6,
  Orbital: 7,
  Collapse: 8,
  /** Focused light: the tier-three beam weapons. */
  Beam: 9,
  /** Long-range indirect fire: the tier-three artillery. */
  Bombard: 10,
  /** A marksman's rifle: lethal to a person, useless against a vehicle. */
  Marksman: 11,
} as const

export type Warhead = (typeof WARHEAD)[keyof typeof WARHEAD]

export const WARHEAD_NAMES = [
  'small-arms',
  'auto-cannon',
  'ap-shell',
  'he-shell',
  'arc',
  'siege',
  'defense-gun',
  'orbital',
  'collapse',
  'beam',
  'bombard',
] as const

/**
 * `MULTIPLIERS[warhead][armorClass]`, as Q16.16.
 *
 * The table is built so that **each faction has a specialist against every
 * armor class** — the validator asserts it, and the first draft failed: nothing
 * was strongest against light armour, and the Ascendancy had no anti-heavy
 * answer at all. A roster with a hole like that plays as one dominant unit,
 * which is the failure the armor matrix exists to prevent.
 *
 * Read the rows as intentions:
 *  - small arms shred infantry and bounce off armour
 *  - autocannon and arc are the two factions' answers to light vehicles
 *  - AP shells and siege mortars are the two answers to heavy
 *  - HE is what an engineer or infiltrator puts against a wall
 */
const TABLE: number[][] = [
  //                infantry  light  heavy  structure
  /* small-arms  */ [1.55, 0.6, 0.25, 0.35],
  /* auto-cannon */ [0.85, 1.45, 0.6, 0.5],
  /* ap-shell    */ [0.4, 1.15, 1.5, 0.8],
  /* he-shell    */ [1.1, 0.9, 0.55, 1.4],
  /* arc         */ [1.0, 1.4, 0.72, 0.6],
  /* siege       */ [0.6, 0.8, 1.5, 1.42],
  /* defense-gun */ [1.35, 1.15, 0.85, 0.4],
  /* orbital     */ [3.0, 3.0, 2.5, 2.2],
  /* collapse    */ [2.6, 2.4, 2.0, 2.6],
  // Beams cut armour and scatter off soft targets. That is what makes the
  // tier-three laser a tank-killer that infantry answer, rather than a strictly
  // better version of everything below it — a late unit with no counter is how
  // a tech tree collapses into one build order.
  /* beam        */ [0.45, 1.2, 1.75, 1.1],
  // Bombardment is for buildings and the things parked next to them. Poor
  // against anything that can move out from under it, which with a shell in
  // flight and a minimum range is most units that see it coming.
  /* bombard     */ [0.75, 0.7, 1.05, 1.75],
  // The most lopsided row in the table, deliberately. A marksman deletes
  // infantry at a range nothing else has, and is worth almost nothing against
  // anything with a hull — so it answers massed infantry and is a liability on
  // its own, which is the only way a unit that outranges everything can exist
  // without being the only unit anyone builds.
  /* marksman    */ [3.2, 0.3, 0.12, 0.15],
]

export const MULTIPLIERS: Int32Array[] = TABLE.map((row) =>
  Int32Array.from(row.map((v) => fix.fromFloat(v))),
)

/**
 * Damage after the armor matrix. Integer throughout — a fractional hit point
 * would be a float in the simulation, and rounding it per-hit rather than
 * accumulating is what keeps two clients agreeing on when a unit dies.
 */
export function resolveDamage(baseDamage: number, warhead: number, armor: number): number {
  const row = MULTIPLIERS[warhead]
  if (!row) return baseDamage
  const scaled = fix.mul(fix.fromInt(baseDamage), fix.raw(row[armor] ?? fix.ONE))
  const out = fix.toInt(scaled)
  // A weapon that connects always does something. Otherwise a hard counter
  // reads as a bug: the shell hits, the health bar does not move.
  return out < 1 ? 1 : out
}

/** The raw matrix entry, as Q16.16. Used by target selection. */
export function multiplierFor(warhead: number, armor: number): fix.Fixed {
  const row = MULTIPLIERS[warhead]
  if (!row) return fix.ONE
  return fix.raw(row[armor] ?? fix.ONE)
}

/** The armor class a warhead is strongest against. Used by the coverage test. */
export function bestAgainst(warhead: number): number {
  const row = MULTIPLIERS[warhead]
  let best = 0
  for (let i = 1; i < ARMOR_COUNT; i++) if (row[i] > row[best]) best = i
  return best
}
